import { Link } from "react-router-dom";
import { ArrowRight, ClipboardCheck, BookOpen, MessageCircleHeart } from "lucide-react";
import { Brand, Motto } from "@/components/Brand";
import { DeveloperCredit } from "@/components/DeveloperCredit";
import { usePrefs } from "@/context/PrefsContext";
import { useAuth } from "@/context/AuthContext";

const IMG = "https://images.unsplash.com/photo-1786805849984-6cb28fc31c9c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Landing() {
  const { t, prefs, update } = usePrefs();
  const { user } = useAuth();
  const startTo = user ? "/dashboard" : "/register";
  const benefits = [
    { icon: ClipboardCheck, title: t("b1t"), desc: t("b1d") },
    { icon: BookOpen, title: t("b2t"), desc: t("b2d") },
    { icon: MessageCircleHeart, title: t("b3t"), desc: t("b3d") },
  ];
  return (
    <div className="paper-grain min-h-screen bg-paper" data-testid="landing-page">
      <header className="relative mx-auto flex max-w-5xl items-center justify-between px-5 py-5 sm:px-8">
        <Brand />
        <div className="flex items-center gap-2">
          <button data-testid="landing-lang-toggle" onClick={() => update({ ui_lang: prefs.ui_lang === "id" ? "jp" : "id" })} className="inline-flex min-h-[40px] items-center rounded-full border border-border px-3.5 py-2 text-xs font-medium transition-colors hover:bg-card">{prefs.ui_lang === "id" ? "日本語" : "ID"}</button>
          <Link data-testid="landing-login-button" to={user ? "/dashboard" : "/login"} className="inline-flex min-h-[40px] items-center rounded-full px-4 py-2 text-sm font-medium transition-colors hover:bg-card">{user ? t("dashboard") : t("login")}</Link>
        </div>
      </header>

      <section className="relative mx-auto grid max-w-5xl items-center gap-12 px-5 pb-20 pt-10 sm:px-8 sm:pt-16 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="fade-up">
          <p className="font-jpserif text-xs tracking-[0.3em] text-muted-foreground">豊か学び · ゆたかまなび</p>
          <Motto size="lg" className="mt-3" testid="landing-motto" />
          <h1 className="mt-5 font-jpserif text-4xl leading-[1.15] sm:text-5xl lg:text-6xl">{t("tagline")}</h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg">{t("sub")}</p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link data-testid="landing-start-button" to={startTo} className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-primary-foreground shadow-[0_10px_30px_-12px_hsl(var(--primary)/0.6)] transition-transform hover:-translate-y-0.5">{t("start")}<ArrowRight className="h-4 w-4" /></Link>
            {!user && <Link data-testid="landing-login-secondary" to="/login" className="inline-flex items-center rounded-full border border-foreground/20 px-7 py-3.5 text-sm font-semibold transition-colors hover:bg-card">{t("login")}</Link>}
          </div>
        </div>
        <div className="relative hidden lg:block fade-up" style={{ animationDelay: "120ms" }}>
          <div className="overflow-hidden rounded-[28px] border border-border shadow-[0_30px_60px_-40px_rgba(31,36,33,0.5)]">
            <img src={IMG} alt="Meja belajar minimalis" className="aspect-[4/5] w-full object-cover" />
          </div>
          <div className="hanko absolute -bottom-5 -left-5 h-16 w-16 rotate-[-6deg] text-3xl shadow-lg">学</div>
        </div>
      </section>

      <section className="relative mx-auto max-w-5xl px-5 pb-24 sm:px-8">
        <div className="grid gap-4 sm:grid-cols-3">
          {benefits.map(({ icon: I, title, desc }, i) => (
            <div key={title} data-testid={`landing-benefit-${i}`} className="soft-card p-6 fade-up" style={{ animationDelay: `${150 + i * 80}ms` }}>
              <I className="h-5 w-5 text-secondary" />
              <h3 className="mt-4 text-base font-semibold">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="relative mx-auto max-w-5xl border-t border-border px-5 py-10 sm:px-8" data-testid="landing-footer">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
          <div><Brand /><Motto className="mt-4" testid="landing-footer-motto" /><p className="mt-3 text-xs text-muted-foreground">© {new Date().getFullYear()} LPK Yutaka Education Center</p></div>
          <DeveloperCredit testid="landing-developer-credit" />
        </div>
      </footer>
    </div>
  );
}
